import { useState } from "react";
import { MailWarning } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { enviarVerificacion } from "@/lib/api";

/**
 * Recordatorio de confirmar el correo, con reenvío a un clic.
 *
 * Se decidió **no bloquear**: quien no ha verificado puede reservar y usar todo
 * desde el primer momento. En un piloto lo que se quiere es que la gente use el
 * sistema, no que tropiece en la puerta si el correo tardó o cayó en spam.
 *
 * El costo de esa decisión es que alguien puede quedarse con un correo mal
 * escrito y no recibir avisos nunca. Por eso el recordatorio es persistente en
 * vez de aparecer una sola vez: molesta lo justo para que se resuelva.
 */
export default function AvisoVerificarCorreo() {
  const { cuenta } = useAuth();
  const [estado, setEstado] = useState<"quieto" | "enviando" | "enviado" | "falló">("quieto");

  // `emailVerified` viene del token. Al confirmar desde el correo, el aviso
  // desaparece en cuanto el token se renueva o la persona vuelve a entrar.
  if (!cuenta || cuenta.emailVerified) return null;

  const reenviar = async () => {
    setEstado("enviando");
    try {
      const r = await enviarVerificacion({});
      setEstado(r.enviado || r.ya_verificado ? "enviado" : "falló");
    } catch {
      setEstado("falló");
    }
  };

  return (
    <div className="contenedor" style={{ paddingTop: "var(--e4)" }}>
      <div className="aviso aviso-alerta fila entre envuelve g3" role="status">
        <span className="fila g2">
          <MailWarning size={17} aria-hidden style={{ flexShrink: 0 }} />
          {estado === "enviado" ? (
            <span>
              Te mandamos el correo a <b>{cuenta.email}</b>. Revisa tu bandeja y también spam.
            </span>
          ) : estado === "falló" ? (
            <span>No pudimos mandar el correo. Intenta de nuevo en un momento.</span>
          ) : (
            <span>
              Falta confirmar tu correo <b>{cuenta.email}</b> para que podamos avisarte de tus reservas.
            </span>
          )}
        </span>

        {estado !== "enviado" && (
          <button className="btn btn-contorno btn-sm" onClick={reenviar} disabled={estado === "enviando"}>
            {estado === "enviando" && <span className="girador" />}
            {estado === "falló" ? "Reintentar" : "Reenviar correo"}
          </button>
        )}
      </div>
    </div>
  );
}
